import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl, useSelector } from 'umi';
import { Segment } from 'tea-component';

import {
  useLocal,
  useOption,
  useWeekDay,
} from '@/pages/dashboard/utils/customHook';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import {
  commonValueFormatter,
  commonDownloadValueFormatter,
  formatFilterValues,
  handleDownload,
} from '@/pages/dashboard/utils/utils';
import { momentFormat } from '@/pages/dashboard/utils/_config/filtersTimeFormat';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import {
  xAxisHighLight,
  xAxisLabelFormat,
} from '@/pages/dashboard/components/Chart/_config/plug';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import {
  TABLE_CELL_PADDING_LEFT,
  TABLE_CELL_PADDING_RIGHT,
} from '@/pages/home/_config/config';
import { getApiData } from '@/pages/dashboard/services/common';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';

function DAULevelDistributionCo(props: any) {
  const { config: propsConfig, className, style } = props;
  const { api, componentMetrics } = propsConfig || {}
  const { type = 'int', decimal = 0 } = componentMetrics || {}
  const contextObj = useContext(FilterContext)
  const { filterVal, filterLoading } = contextObj || {};
  const { gameCode } = useSelector((state: any) => {
    return {
      gameCode: state?.dashboard?.currentGame?.game_code,
    }
  });
  const [levelData, setLevelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tabname, setTabname] = useState('accumulated');
  const { getWeekDay } = useWeekDay();
  const { isEN, locale, getLocalText } = useLocal(
    'engagement_active_activeUsers_distribution',
  );
  const { formatMessage } = useIntl();
  const timeType = filterVal?.timeType || 'Day';
  const hasMonth = PERIODTIMEOBJ[timeType] === 'monthly';

  const { title }: { title: string } = useMemo(() => {
    const titleResultObj: any = {
      day: componentMetrics?.[isEN ? 'enName' : 'cnName'],
      week: componentMetrics?.[isEN ? 'enWeekName' : 'cnWeekName'],
      month: componentMetrics?.[isEN ? 'enMonthName' : 'cnMonthName'],
    }
    return {
      title: titleResultObj?.[timeType?.toLocaleLowerCase()],
    }
  }, [locale, timeType, componentMetrics])

  const [data, setData] = useState({
    chart: [] as Array<{}>,
    table: {
      option: [] as Array<{}>,
      data: [] as Array<{}>,
      downloadData: [] as Array<{}>,
    },
  } as any);

  const tabs = [
    { text: getLocalText(`tab1`), value: 'accumulated' },
    { text: getLocalText(`tab2`), value: 'specific' },
  ];

  function formatterFunc(list: any[]) {
    let temp = {
      title: ' ',
      metricsName: getLocalText('chart_title'),
      data: [] as Array<{}>,
    };
    list.forEach(
      (ele: { value: string | number; seriesName: any; color: any }) => {
        if (ele.value === '' || ele.value == 0) return;

        temp.data.push({
          name: ele.seriesName,
          metricsNum: commonValueFormatter(type, ele.value, decimal),
          color: ele.color,
        });
      },
    );

    if (temp.data?.length <= 0) return '<div></div>';
    return getEchartTooltipText(temp);
  }

  useEffect(() => {
    if (!filterVal) return;
    !filterLoading && getEngagementActiveMetrics();
  }, [filterVal, tabname, filterLoading]);

  const getEngagementActiveMetrics = () => {
    const { rangeTime, diff, timeType, ...filters } = filterVal || {};
    const params = {
      game_code: gameCode,
      start_time: rangeTime?.[0],
      end_time: rangeTime?.[1],
      date_type: PERIODTIMEOBJ?.[timeType] || 'daily',
      by_type: tabname,
      filters: formatFilterValues(filters),
    };
    setLoading(true);
    getApiData(api, params).then((res: any) => {
      setLoading(false);
      setError(false);
      setLevelData(res);
    })
      .catch((err: any) => {
        setError(true);
        setLoading(false);
        setLevelData(null);
      });
  };

  useEffect(() => {
    if (!levelData) return;
    handleData(levelData || null);
  }, [levelData, locale]);

  function handleAxisFormat(val: string) {
    return hasMonth ? momentFormat(val.split('(')[0], 'YYYY-MM') : momentFormat(val.split('(')[0], 'MM-DD');

  }

  function handleData(result: null) {
    if (result === null) {
      setData({
        chart: [],
        table: { option: [], data: [], downloadData: [] },
      });
      return;
    }

    let list = result?.dua_level_dis || [];
    let heads =
      result?.[tabname === 'accumulated' ? 'acu_head' : 'spc_head'] || [];
    let tHead: Array<{}> = [
      {
        key: 'date',
        header: getLocalText('date'),
        align: 'left',
        fixed: 'left',
        width: 120 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
        render: (tr) => {
          const weekDay = getWeekDay(tr?.date);
          const isSunOrSat =
            weekDay === 'Sun' ||
            weekDay === 'Sat' ||
            weekDay === '周日' ||
            weekDay === '周六';
          return (
            <>
              <span className="table-text">{tr?.date}</span>
              {timeType !== 'Month' && (
                <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>
                  {weekDay}
                </span>
              )}
            </>
          );
        },
      },
    ];
    let tData: Array<{}> = [];
    let tDownloadData: Array<{}> = [];

    // 表头数据
    heads.forEach((ele: string | number) => {
      let field = {
        key: ele,
        header: ele,
        align: 'right',
        width: 80 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
        render: (tr: any) => <span>{tr[ele]}</span>,
      };
      tHead.push(field);
    });

    // 表格数据
    list.forEach((ele: { date: any; kv_list: never[] }) => {
      let kvs: any = ele.kv_list || [];
      let len: any = kvs.length,
        i,
        val;
      let temp: any = {
        date: hasMonth
          ? momentFormat(ele.date, 'YYYY-MM')
          : momentFormat(ele.date, 'YYYY-MM-DD'),
      };
      let downloadTemp: any = {
        date: hasMonth
          ? momentFormat(ele.date, 'YYYY-MM')
          : momentFormat(ele.date, 'YYYY-MM-DD'),
      };

      heads?.forEach((k: string | number) => {
        val = '';
        for (i = len; i--;) {
          if (kvs[i].key === k) {
            val = kvs[i].value;
            break;
          }
        }
        temp[k] = '' + commonValueFormatter(type, val, decimal);
        downloadTemp[k] = commonDownloadValueFormatter(type, val, decimal);
      });

      len && tData.push(temp) && tDownloadData.push(downloadTemp);
    });

    // 图表
    const chartData: {
      name: string;
      stack: string;
      data: any[];
    }[] = [];

    heads?.forEach((level: any) => {
      const data: {
        x: string;
        y: string | number;
      }[] = [];
      list?.forEach((listItem: { [x: string]: any[]; date: any }) => {
        const date = listItem?.date;
        const value = listItem?.[
          tabname === 'accumulated' ? 'kv_list' : 'top10'
        ]?.filter((item: { key: any }) => item?.key === level)?.[0]?.value;

        data.push({
          x: hasMonth
            ? momentFormat(date, 'YYYY-MM')
            : momentFormat(date, 'YYYY-MM-DD'),
          y: Math.round(value) || value,
        });
      });
      chartData.push({
        name: level,
        stack: 'stack',
        data,
      });
    });

    setData({
      chart:
        tabname === 'accumulated'
          ? chartData
          : chartData?.filter((item) =>
            item?.data?.some((dataItem) => dataItem?.y),
          ),
      table: { option: tHead, data: tData, downloadData: tDownloadData },
    });
  }

  const config = {
    bar: {
      formatterFunc,
    },
    line: {
      formatterFunc,
    },
    table: {
      isSortable: true,
      setSortableConfig: (sorts: any, setSorts: (arg0: any) => any) => {
        return {
          columns: data?.table?.option?.map((e: any) => {
            return {
              key: e.key,
              prefer: 'desc',
              sorter: (
                f: {
                  [x: string]: {
                    replace: (arg0: RegExp, arg1: string) => number;
                  };
                },
                s: {
                  [x: string]: {
                    replace: (arg0: RegExp, arg1: string) => number;
                  };
                },
              ) =>
                f[e.key].replace(/[,-]/g, '') - s[e.key].replace(/[,-]/g, ''),
            };
          }),
          value: sorts,
          onChange: (value: any) => setSorts(value),
        };
      },
    },
  };
  const option = data || {};
  const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(option);
  const renderList = [
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: (
        <BarChart
          height={340}
          legendData={xAxisData.length > 0 ? legendData : []}
          xAxisData={xAxisData || []}
          series={xAxisData.length > 0 ? barSeries : []}
          formatterFunc={config?.bar?.formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            if (config?.bar?.setOtherConfigFunc)
              opt = config?.bar?.setOtherConfigFunc(opt);
            opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
            opt = xAxisHighLight(opt, {
              formatter: (params: { value: string }) => {
                return handleAxisFormat(params.value);
              },
            });
            return opt;
          }}
        />
      ),
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: (
        <LineChart
          height={340}
          legendData={xAxisData.length > 0 ? legendData : []}
          xAxisData={xAxisData || []}
          series={xAxisData.length > 0 ? lineSeries : []}
          formatterFunc={config?.line?.formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            if (config?.line?.setOtherConfigFunc)
              opt = config?.line?.setOtherConfigFunc(opt);
            opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
            opt = xAxisHighLight(opt, {
              formatter: (params: { value: string }) => {
                return handleAxisFormat(params.value);
              },
            });
            return opt;
          }}
        />
      ),
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: (
        <MiniTable
          height={340}
          loading={false}
          tableConfig={config?.table}
          tableOption={option?.table?.option}
          tableList={option?.table?.data}
        />
      ),
    },
  ];

  return (
    <div className={className} style={style}>
      <CardBox
        className="register-table"
        height={340}
        bordered={false}
        titleCom={title}
        subTitleCom={
          <Segment
            rimless={false}
            value={tabname}
            onChange={(value) => setTabname(value)}
            options={tabs}
          />
        }
        renderList={renderList}
        defaultAvtive={0}
        downloadClick={async () => {
          handleDownload(option?.table, title);
        }}
        isLoading={loading}
        isError={error}
        isNodata={isNodata}
      />
    </div>
  );
}

export default DAULevelDistributionCo;
